import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import HoverLink from './HoverLink'
import AsciiBox from './AsciiBox'
import { isChrome, isMobile } from 'react-device-detect';

const Research = () => {
    const [time, setTime] = useState(0);
    const requestRef = useRef();

    const animate = useCallback((t) => {
        setTime(t * 0.001);
        requestRef.current = requestAnimationFrame(animate);
    }, []);

    useEffect(() => {
        requestRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(requestRef.current);
    }, [animate]);

    const neuralNetArt = useMemo(() => {
        const W = 80;
        const H = 172;
        const grid = Array.from({ length: H }, () => new Array(W).fill(' '));
        const prio = Array.from({ length: H }, () => new Array(W).fill(0));

        const put = (x, y, c, p = 1) => {
            const xi = Math.round(x), yi = Math.round(y);
            if (xi >= 0 && xi < W && yi >= 0 && yi < H && p > prio[yi][xi]) {
                grid[yi][xi] = c;
                prio[yi][xi] = p;
            }
        };

        const layers = [
            { y: 12,  xs: [12, 23, 34, 45, 56] },
            { y: 58,  xs: [9, 20, 31, 42, 53, 64] },
            { y: 108, xs: [9, 20, 31, 42, 53, 64] },
            { y: 155, xs: [13, 25, 38, 51, 63] },
        ];

        const topY = layers[0].y;
        const botY = layers[layers.length - 1].y;
        const range = botY - topY;

        const period = 3.2;
        const ph = ((time % period) + period) % period / period;
        const cycleNum = Math.floor(time / period);

        let fwdProg = 0, bwdProg = 0;
        let mode = 'idle';

        if (ph < 0.20) {
            fwdProg = ph / 0.20;
            mode = 'fwd';
        } else if (ph < 0.33) {
            fwdProg = 1.0;
            mode = 'fwd_hold';
        } else if (ph < 0.53) {
            fwdProg = 1.0;
            bwdProg = (ph - 0.33) / 0.20;
            mode = 'bwd';
        } else {
            mode = 'idle';
        }

        const fwdY = topY + fwdProg * range;
        const bwdY = botY - bwdProg * range;

        const PW = 6;

        // Two independent cycle seeds for uncorrelated threshold variation
        const cycleSeed1 = Math.abs(Math.sin(cycleNum * 9301 + 49297));
        const cycleSeed2 = Math.abs(Math.sin(cycleNum * 6271 + 31337));
        const strongThresh = 0.65 + cycleSeed1 * 0.25; // 0.65 → 0.90
        const weakThresh   = 0.25 + cycleSeed2 * 0.30; // 0.25 → 0.55 (sparser base)

        // Per-layer brightness shift: different layers light up each cycle
        const layerShift = (l) => Math.sin(cycleNum * 9301 + l * 7919) * 0.38;

        // Hash scrambles completely each cycle; layer shift biases whole layer up/down
        const connWeight = (l, si, di) => {
            const s = l * 2003 + si * 1009 + di * 7;
            const raw = Math.abs(Math.sin(s * 127.1 + cycleNum * 5173.3 + 311.7));
            return Math.max(0, Math.min(1, raw + layerShift(l)));
        };

        for (let l = 0; l < layers.length - 1; l++) {
            const src = layers[l];
            const dst = layers[l + 1];

            for (let si = 0; si < src.xs.length; si++) {
                const sx = src.xs[si];
                for (let di = 0; di < dst.xs.length; di++) {
                    const dx = dst.xs[di];

                    const w = connWeight(l, si, di);
                    const strong = w > strongThresh;
                    const weak = w < weakThresh;

                    const y0 = src.y + 1;
                    const y1 = dst.y - 1;
                    const span = y1 - y0;
                    if (span <= 0) continue;

                    for (let yi = y0; yi <= y1; yi++) {
                        const t = (yi - y0) / span;
                        const xi = sx + t * (dx - sx);

                        const slopeH = (dx - sx) / span;
                        const base = Math.abs(slopeH) < 0.2 ? '|' : slopeH > 0 ? '\\' : '/';

                        const fi = mode === 'fwd' ? Math.max(0, 1 - Math.abs(yi - fwdY) / PW) : 0;
                        const bi = mode === 'bwd' ? Math.max(0, 1 - Math.abs(yi - bwdY) / PW) : 0;

                        const inPulse = fi > 0.3 || bi > 0.3;
                        const fwdPassed = yi < fwdY;
                        const bwdNotYet = yi < bwdY;

                        if (inPulse) {
                            if (!weak) put(xi, yi, base, 2);
                        } else {
                            const showTrail =
                                (mode === 'fwd' && fwdPassed) ||
                                (mode === 'fwd_hold') ||
                                (mode === 'bwd' && bwdNotYet);

                            if (showTrail) {
                                // strong: use full structural char (clearly visible)
                                // normal: faint dot
                                // weak: invisible
                                if (strong) put(xi, yi, base, 1);
                                else if (!weak) put(xi, yi, '.', 1);
                            }
                        }
                    }
                }
            }
        }

        // Draw nodes
        for (const layer of layers) {
            let nc;
            if (mode === 'fwd') nc = fwdY >= layer.y ? 'O' : 'o';
            else if (mode === 'fwd_hold') nc = 'O';
            else if (mode === 'bwd') nc = bwdY <= layer.y ? 'o' : 'O';
            else nc = 'o';

            for (const nx of layer.xs) {
                put(nx, layer.y, nc, 4);
            }
        }

        return grid.map(row => row.join('')).join('\n');
    }, [time]);

    return (
        <div style={{ display: "flex", gap: "80px", alignItems: "flex-start", justifyContent: "center", marginTop: "10%", flexDirection: isMobile ? "column" : "row" }}>
            <div style={{ flex: "0 0 auto", width: isMobile ? "100%" : "400px", margin: isMobile ? "0 auto" : "0" }}>
                {isChrome ? (
                    <AsciiBox width={150} height={32}>Research</AsciiBox>
                ) : (
                    <div className="nameBoxDiv">
                    </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    <div>
                        <p style={{ marginBottom: "1px" }}><b>Staggered Environment Resets Improve Massively Parallel On-Policy Reinforcement Learning</b></p>
                        <p style={{ margin: "0", fontSize: "12px" }}><i>Sid Bharthulwar*, Stone Tao, Hao Su</i></p>
                        <p style={{ margin: "0", fontSize: "11px", color: "#666" }}><HoverLink text="NeurIPS 2025" href="https://neurips.cc/Conferences/2025" />, <HoverLink text="Finding the Frame @ RLC 2025" href="https://rl-conference.cc/" /></p>
                    </div>

                    <div>
                        <p style={{ marginBottom: "1px" }}><b>From Extrapolation to Generalization: How Conditioning Transforms Symmetry Learning in Diffusion Models</b></p>
                        <p style={{ margin: "0", fontSize: "12px" }}><i>Sid Bharthulwar*, T. Anderson Keller, Manos Theodosis, Demba E. Ba</i></p>
                        <p style={{ margin: "0", fontSize: "11px", color: "#666" }}><HoverLink text="NeurReps @ NeurIPS 2025" href="https://neurreps.org/" /></p>
                    </div>

                    <div>
                        <p style={{ marginBottom: "1px" }}><b>Restoring Task-Relevant Information in Synthetic Data: A Small-Scale V-Information View</b></p>
                        <p style={{ margin: "0", fontSize: "12px" }}><i>Sid Bharthulwar*</i></p>
                        <p style={{ margin: "0", fontSize: "11px", color: "#666" }}><HoverLink text="MOSS @ ICML 2025" href="https://icml.cc/Conferences/2025" /></p>
                    </div>
                    <div>
                        <p style={{ marginBottom: "1px" }}><b>Evolutionary Prompt Optimization Discovers Emergent Multimodal Reasoning Strategies in Vision-Language Models</b></p>
                        <p style={{ margin: "0", fontSize: "12px" }}><i>Sid Bharthulwar*, John Rho*, Katrina Brown*</i></p>
                        <p style={{ margin: "0", fontSize: "11px", color: "#666" }}><HoverLink text="Reasoning and Planning for LLMs @ ICLR 2025" href="https://iclr.cc/Conferences/2025" /></p>
                    </div>
                    <div>
                        <p style={{ marginBottom: "1px" }}><b>Convolutional Encoder-Decoder Networks for Volumetric CT Scout Reconstructions from Single and Dual-View Radiographs</b></p>
                        <p style={{ margin: "0", fontSize: "12px" }}><i>Sid Bharthulwar, Peter Noël, Nadav Shapira</i></p>
                        <p style={{ margin: "0", fontSize: "11px", color: "#666" }}><HoverLink text="16th International Meeting on Fully 3D Image Reconstruction in Radiology and Nuclear Medicine" href="https://arxiv.org/pdf/2110.04143" /></p>
                    </div>
                </div>

                <br />
                <p>
                    <HoverLink routing="/" text="← Back" />
                </p>
            </div>

            {!isMobile && (
                <div style={{
                    flex: "0 0 auto",
                    fontFamily: "monospace",
                    fontSize: "6px",
                    lineHeight: "0.6",
                    letterSpacing: "0px",
                    color: "inherit",
                    opacity: "0.6",
                    whiteSpace: "pre",
                    overflow: "visible",
                    userSelect: "none",
                    paddingTop: "20px",
                }}>
                    {neuralNetArt}
                </div>
            )}
        </div>
    )
}

export default Research
